import React, { useEffect, useMemo, useState } from "react";
import { Button, Alert, Spinner, Card, ButtonGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BillingClient, FileResponse, StartCheckoutDto } from "../web-api-client.ts";

type BillingStatus =
  | "pending_setup"
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "cancel_scheduled";

type BillingInterval = "month" | "year";

type BillingStatusResponse = {
  status: BillingStatus;
  amountCents: number;
  currency: string;
  billingInterval?: BillingInterval | string | null;
  currentPeriodEnd?: string | null;
};

async function fileResponseToJson<T>(fr: FileResponse): Promise<T> {
  const text = await fr.data.text();
  return JSON.parse(text) as T;
}

function normalizeInterval(v: any): BillingInterval {
  return String(v || "month").toLowerCase() === "year" ? "year" : "month";
}

function computeAnnualCentsFromMonthly(monthlyCents: number) {
  return Math.round(monthlyCents * 12 * 0.9);
}

function formatMoney(cents: number, currency: string) {
  const amount = cents / 100;
  return amount.toLocaleString(undefined, { style: "currency", currency: currency.toUpperCase() });
}

const Activate: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [data, setData] = useState<BillingStatusResponse | null>(null);
  const [interval, setInterval] = useState<BillingInterval>("month");
  const [error, setError] = useState<string | null>(null);

  const billingClient = useMemo(() => new BillingClient(), []);

  async function fetchBillingStatus(): Promise<BillingStatusResponse> {
    const res = await fetch("/api/Billing/status", { credentials: "include" });
    if (!res.ok) throw new Error("Unable to fetch billing status.");
    return res.json();
  }

  async function startCheckout(selected: BillingInterval) {
    setError(null);
    setWorking(true);
    try {
      const dto: StartCheckoutDto = {
        dealerId: undefined as any, // inferred server-side
        billingInterval: selected
      };
      const fr = await billingClient.billing_StartCheckout(dto);
      const { sessionUrl } = await fileResponseToJson<{ sessionUrl: string }>(fr);
      window.location.href = sessionUrl;
    } catch (e: any) {
      setError(e?.message ?? "Failed to start checkout.");
      setWorking(false);
    }
  }

  useEffect(() => {
    const autoStart = false; // keep false so dealer can choose interval

    (async () => {
      try {
        const s = await fetchBillingStatus();
        setData(s);

        const normalized = normalizeInterval(s.billingInterval);
        setInterval(normalized);

        if (s.status === "active" || s.status === "trialing") {
          navigate("/");
          return;
        }

        if (autoStart && (s.status === "pending_setup" || s.status === "canceled")) {
          await startCheckout(normalized);
          return;
        }
      } catch (e: any) {
        setError(e?.message ?? "Error while activating.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthlyCents = data?.amountCents ?? 0;
  const currency = data?.currency ?? "usd";
  const annualCents = computeAnnualCentsFromMonthly(monthlyCents);

  const canStartCheckout =
    data?.status === "pending_setup" || data?.status === "canceled";

  const isPastDue = data?.status === "past_due";
  const isCancelScheduled = data?.status === "cancel_scheduled";

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
      <Card className="p-4" style={{ maxWidth: 560, width: "100%" }}>
        <h3 className="mb-3">Activate your account</h3>

        {loading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" role="status" />
            <div>Loading billing…</div>
          </div>
        ) : (
          <>
            {error && <Alert variant="danger" className="mt-2">{error}</Alert>}

            {data && (data.status === "active" || data.status === "trialing") && (
              <Alert variant="success" className="mt-2">
                Your subscription is active. Redirecting…
              </Alert>
            )}

            {isCancelScheduled && (
              <Alert variant="info" className="mt-2">
                Your subscription is scheduled to end
                {data.currentPeriodEnd ? (
                  <>
                    {" "}on <strong>{new Date(data.currentPeriodEnd).toLocaleString()}</strong>.
                  </>
                ) : (
                  "."
                )}{" "}
                You can manage billing from the Billing page.
              </Alert>
            )}

            {canStartCheckout && (
              <>
                <p className="text-muted mb-3">
                  Choose a plan to start using QCAR.
                </p>

                <div className="mb-3">
                  <div className="fw-semibold mb-2">Billing interval</div>
                  <ButtonGroup className="w-100">
                    <Button
                      variant={interval === "month" ? "primary" : "outline-primary"}
                      onClick={() => setInterval("month")}
                      disabled={working}
                    >
                      Monthly ({formatMoney(monthlyCents, currency)}/mo)
                    </Button>
                    <Button
                      variant={interval === "year" ? "primary" : "outline-primary"}
                      onClick={() => setInterval("year")}
                      disabled={working}
                    >
                      Annual ({formatMoney(annualCents, currency)}/yr, save 10%)
                    </Button>
                  </ButtonGroup>
                </div>

                <Button
                  onClick={() => startCheckout(interval)}
                  disabled={working}
                >
                  {working ? "Redirecting…" : "Continue to Checkout"}
                </Button>
              </>
            )}

            {isPastDue && (
              <Alert variant="warning" className="mt-2">
                Your subscription is past due. Please update your payment method from the Billing page.
              </Alert>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default Activate;